<script setup lang="ts">
import type { LeaderboardBreakdownRecordType, MarketMakerDetailsType, PerformanceMetricsForMmType } from '@forgd/contract'
import type { ColumnDef } from '@tanstack/vue-table'
import LClaimOrganizationBanner from '#leaderboards/components/LClaimOrganizationBanner.vue'
import LConnectWithMarketMakerModal from '#leaderboards/components/LConnectWithMarketMakerModal.vue'
import LPerformanceSummary from '#leaderboards/components/LPerformanceSummary.vue'
import { useLeaderboardStore } from '#leaderboards/store/useLeaderboardStore'
import { getIntegrationBadge, isNotFullyIntegrated } from '#leaderboards/utils/integration'
import { countries } from '@forgd/shared/data/countries'
import { EngagementOptionLabels } from '@forgd/shared/labels'
import { Alert, AlertDescription, AlertTitle } from '@forgd/ui-kit/alert'
import { Badge } from '@forgd/ui-kit/badge'
import { LoadingAnimation } from '@forgd/ui-kit/blocks/animations'
import { ExchangeAvatar } from '@forgd/ui-kit/blocks/avatars'
import { ColumnCell, ColumnHeader, DataTable } from '@forgd/ui-kit/blocks/data-table'
import { NoData } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent } from '@forgd/ui-kit/card'
import { ArrowUpRightIcon, ExclamationCircleIcon, GlobeAltIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'
import LMetricValueCell from './cell/LMetricValueCell.vue'

const props = defineProps<{
  marketMakerId: string
  /**
   * When provided (public RFQ demo), renders from this data instead of querying
   * the live endpoints; null sections render the usual limited-data states.
   */
  staticData?: {
    details: MarketMakerDetailsType | null
    performance: PerformanceMetricsForMmType | null
    breakdown: LeaderboardBreakdownRecordType | null
  } | null
}>()

interface LeaderboardBreakdownColumns {
  metric: string
  value: number | null
  percentile: number
  rank: number
  formatOption: 'percent:2' | 'currency:0'
}

const auth = useAuth()
const store = useLeaderboardStore()

const areQueriesEnabled = computed(() => {
  return Boolean(props.marketMakerId) && !props.staticData
})

const { data: livePerformanceMetrics, isLoading: isPerformanceMetricsLoading } = useQuery({
  ...buildApiQuery('/market-makers-leaderboard/performance/{marketMakerId}', {
    params: () => ({
      path: { marketMakerId: props.marketMakerId },
      query: { ...store.dateRange, projectId: auth.project?.id },
    }),
  }),
  enabled: () => areQueriesEnabled.value,
  refetchOnWindowFocus: false,
})

const { data: liveMarketMaker, status: marketMakerDataLoadingStatus } = useQuery({
  ...buildApiQuerySSRSafe('/market-makers/{marketMakerId}', {
    params: () => ({
      path: {
        marketMakerId: props.marketMakerId,
      },
    }),
  }),
  enabled: () => areQueriesEnabled.value,
})

const leaderboardQueriesParams = computed(() => ({
  path: { marketMakerId: props.marketMakerId },
  query: { ...store.dateRange, projectId: auth.project?.id },
}))

const { data: liveLeaderboardBreakdownData, isLoading: isLeaderboardBreakdownLoading } = useQuery({
  ...buildApiQuery('/market-makers-leaderboard/breakdown/{marketMakerId}', {
    params: () => leaderboardQueriesParams.value,
  }),
  enabled: () => areQueriesEnabled.value,
  refetchOnWindowFocus: false,
})

const performanceMetrics = computed(() => (props.staticData ? props.staticData.performance : livePerformanceMetrics.value))
const marketMaker = computed(() => (props.staticData ? props.staticData.details : liveMarketMaker.value))
const marketMakerStatus = computed(() => {
  if (props.staticData) {
    return props.staticData.details ? 'success' : 'error'
  }
  return marketMakerDataLoadingStatus.value
})
const leaderboardBreakdownData = computed(() => (props.staticData ? props.staticData.breakdown : liveLeaderboardBreakdownData.value))

const isLoading = computed(() => {
  if (props.staticData) {
    return false
  }
  return marketMakerDataLoadingStatus.value === 'pending'
    || isLeaderboardBreakdownLoading.value
    || isPerformanceMetricsLoading.value
})

const integrationBadge = computed(() => {
  if (performanceMetrics.value?.integrationStatus == null) {
    return null
  }

  return getIntegrationBadge(performanceMetrics.value.integrationStatus)
})

const notFullyIntegrated = computed(() => isNotFullyIntegrated(performanceMetrics.value?.integrationStatus))

const showClaimBanner = computed(() => {
  return useRuntimeConfig().public.portal === 'app'
    && !props.staticData
    && performanceMetrics.value?.integrationStatus === 'none'
})

const isConnectModalOpen = ref(false)

const showConnectCta = computed(() => {
  return useRuntimeConfig().public.portal === 'app' && !props.staticData
})

const countryLabel = computed(() => {
  const code = marketMaker.value?.countryCode
  const country = countries.find(c => c.value === code)

  if (!country) {
    return null
  }

  return country.label
})

const countryCode = computed(() => marketMaker.value?.countryCode?.toLowerCase() ?? null)

const exchangeSections = computed(() => [
  { title: 'Major CEXs Supported', items: marketMaker.value?.supportedCEX ?? [] },
  { title: 'Major DEXs Supported', items: marketMaker.value?.supportedDEX ?? [] },
].filter(section => section.items.length > 0))

// map MM data for breakdown tables
const depthLeaderboard = computed<LeaderboardBreakdownColumns[]>(() => {
  if (!leaderboardBreakdownData.value?.depthRank) {
    return []
  }

  return [
    { metric: 'Bid Depth 50 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.bidDepth50 },
    { metric: 'Ask Depth 50 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.askDepth50 },
    { metric: 'Bid & Ask Depth 50 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.bidAskDepth50 },
    { metric: 'Bid Depth 50 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidDepth50MarketPercentage },
    { metric: 'Ask Depth 50 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.askDepth50MarketPercentage },
    { metric: 'Bid & Ask Depth 50 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth50MarketPercentage },
    { metric: 'Uptime %, Bid & Ask Depth 50 bps', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth50Uptime },
    { metric: 'Loan Utilization, Bid & Ask Depth 50 bps', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth50LoanUtilization },

    { metric: 'Bid Depth 100 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.bidDepth100 },
    { metric: 'Ask Depth 100 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.askDepth100 },
    { metric: 'Bid & Ask Depth 100 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.bidAskDepth100 },
    { metric: 'Bid Depth 100 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidDepth100MarketPercentage },
    { metric: 'Ask Depth 100 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.askDepth100MarketPercentage },
    { metric: 'Bid & Ask Depth 100 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth100MarketPercentage },
    { metric: 'Uptime %, Bid & Ask Depth 100 bps', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth100Uptime },
    { metric: 'Loan Utilization, Bid & Ask Depth 100 bps', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth100LoanUtilization },

    { metric: 'Bid Depth 200 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.bidDepth200 },
    { metric: 'Ask Depth 200 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.askDepth200 },
    { metric: 'Bid & Ask Depth 200 bps (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.bidAskDepth200 },
    { metric: 'Bid Depth 200 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidDepth200MarketPercentage },
    { metric: 'Ask Depth 200 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.askDepth200MarketPercentage },
    { metric: 'Bid & Ask Depth 200 bps (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth200MarketPercentage },
    { metric: 'Uptime %, Bid & Ask Depth 200 bps', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth200Uptime },
    { metric: 'Loan Utilization, Bid & Ask Depth 200 bps', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth200LoanUtilization },
  ]
})

const volumeLeaderboard = computed<LeaderboardBreakdownColumns[]>(() => {
  if (!leaderboardBreakdownData.value?.volumeRank) {
    return []
  }

  return [
    { metric: 'Maker Fill Volume (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.volumeMaker },
    { metric: 'Taker Fill Volume (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.volumeTaker },
    { metric: 'Maker & Taker Fill Volume (USD)', formatOption: 'currency:0', ...leaderboardBreakdownData.value.volumeTotal },

    { metric: 'Maker Fill Volume (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.volumeMakerMarketPercentage },
    { metric: 'Taker Fill Volume (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.volumeTakerMarketPercentage },
    { metric: 'Maker & Taker Fill Volume (% of Market)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.volumeTotalMarketPercentage },

    { metric: 'Loan Utilization (Maker Fill Volume)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.volumeMakerLoanUtilization },
    { metric: 'Loan Utilization (Taker Fill Volume)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.volumeTakerLoanUtilization },
    { metric: 'Loan Utilization (Maker & Taker Fill Volume)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.volumeTotalLoanUtilization },
  ]
})

const spreadLeaderboard = computed<LeaderboardBreakdownColumns[]>(() => {
  if (!leaderboardBreakdownData.value?.spreadRank) {
    return []
  }

  return [
    { metric: 'Volume Weighted Bid-Ask Spread (%)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskSpread },
    { metric: 'Uptime %, Bid-Ask Spread (%)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.spreadUptime },
  ]
})

const kpiLeaderboard = computed<LeaderboardBreakdownColumns[]>(() => {
  if (!leaderboardBreakdownData.value?.kpiRank) {
    return []
  }

  return [
    { metric: 'Bid-Ask Depth 50 bps (USD)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth50Uptime },
    { metric: 'Bid-Ask Depth 100 bps (USD)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth100Uptime },
    { metric: 'Bid-Ask Depth 200 bps (USD)', formatOption: 'percent:2', ...leaderboardBreakdownData.value.bidAskDepth200Uptime },
    { metric: 'Bid-Ask Spread', formatOption: 'percent:2', ...leaderboardBreakdownData.value.spreadUptime },
  ]
})

const breakdownSections = computed(() => [
  { title: 'Depth KPIs, Detailed Breakdown', rows: depthLeaderboard.value },
  { title: 'Volume KPIs, Detailed Breakdown', rows: volumeLeaderboard.value },
  { title: 'Spread KPIs, Detailed Breakdown', rows: spreadLeaderboard.value },
  { title: 'KPI Adherence, Detailed Breakdown', rows: kpiLeaderboard.value },
])

const columns: ColumnDef<LeaderboardBreakdownColumns>[] = [
  {
    accessorKey: 'metric',
    size: 180,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Metric' }),
    cell: ({ row }) => h(ColumnCell, { value: row.original.metric }),
  },
  {
    accessorKey: 'value',
    size: 90,
    enableSorting: true,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Value' }),
    cell: ({ row }) => h(LMetricValueCell, {
      valueCell: row.original,
      valueFormat: row.original.formatOption,
      viewMode: 'value',
    }),
  },
  {
    accessorKey: 'percentile',
    size: 90,
    enableSorting: true,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Percentile' }),
    cell: ({ row }) => h(LMetricValueCell, {
      valueCell: row.original,
      valueFormat: row.original.formatOption,
      viewMode: 'percentile',
    }),
  },
  {
    accessorKey: 'rank',
    size: 90,
    enableSorting: true,
    invertSorting: true,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Rank' }),
    cell: ({ row }) => h(LMetricValueCell, {
      valueCell: row.original,
      valueFormat: row.original.formatOption,
      viewMode: 'rank',
    }),
  },
]

const pinnedColumns = ['metric' as const]
</script>

<template>
  <div v-if="isLoading" class="min-h-[600px] flex items-center justify-center">
    <LoadingAnimation />
  </div>
  <div v-else-if="marketMakerStatus === 'error'">
    <Alert variant="error">
      <ExclamationCircleIcon />
      <AlertTitle>Error</AlertTitle>
      <AlertDescription>
        <p>An error occurred while loading market maker details.</p>
      </AlertDescription>
    </Alert>
  </div>

  <div v-else-if="marketMakerStatus === 'success' && marketMaker" class="flex flex-col text-sm gap-5 text-secondary">
    <h3 class="text-sm font-semibold text-primal">
      About {{ marketMaker.name }}
    </h3>
    <div>{{ marketMaker.description }}</div>

    <div class="flex items-center gap-2">
      <Badge v-if="integrationBadge" size="md" :variant="integrationBadge.color">
        {{ integrationBadge.label }}
      </Badge>

      <Badge v-if="countryLabel" size="md" variant="gray">
        <span class="flex items-center gap-1">
          HQ: <span v-if="countryCode" class="fi text-xs mr-0.5 rounded-xs overflow-hidden shadow-xs" :class="`fi-${countryCode}`" /> {{ countryLabel }}
        </span>
      </Badge>

      <Button
        v-if="marketMaker.website"
        as-child
        variant="outline"
        size="sm"
      >
        <a :href="marketMaker.website" target="_blank" rel="noopener noreferrer">
          <GlobeAltIcon />
          Website
          <ArrowUpRightIcon class="shrink-0" />
        </a>
      </Button>
    </div>

    <Button v-if="showConnectCta" size="md" class="self-start" @click="isConnectModalOpen = true">
      Connect with this Market Maker
    </Button>
    <LConnectWithMarketMakerModal
      v-if="showConnectCta"
      v-model:open="isConnectModalOpen"
      :market-maker-id="marketMakerId"
      :market-maker-name="marketMaker.name"
    />

    <LClaimOrganizationBanner v-if="showClaimBanner" :market-maker-id="marketMakerId" />

    <div class="w-full border-t border-neutral-600" />

    <LPerformanceSummary
      :aggregated="leaderboardBreakdownData?.aggregatedRank"
      :depth="leaderboardBreakdownData?.depthRank"
      :volume="leaderboardBreakdownData?.volumeRank"
      :spread="leaderboardBreakdownData?.spreadRank"
      :kpi="leaderboardBreakdownData?.kpiRank"
      :projects-count="marketMaker.activeEngagementsCount"
      :average-fdv="marketMaker.averageFDV"
      :performance-metrics="performanceMetrics"
    />

    <template v-for="(section, index) in breakdownSections" :key="section.title">
      <template v-if="!notFullyIntegrated && section.rows.length > 0">
        <div class="flex items-center gap-2 pt-4" :class="index === 0 && 'border-t border-neutral-600'">
          <h3 class="text-sm font-semibold text-primal">
            {{ section.title }}
          </h3>
          <Badge size="sm">
            {{ store.timeframeLabel }}
          </Badge>
        </div>
        <Card class="overflow-hidden">
          <CardContent class="p-0">
            <DataTable
              :columns="columns"
              :pinned-columns="pinnedColumns"
              :data="section.rows"
            />
          </CardContent>
        </Card>
      </template>
    </template>

    <div v-if="notFullyIntegrated" class="rounded-lg border border-neutral-600 bg-neutral-600/30 p-5">
      <NoData
        title="This Market Maker is not integrated on Forgd"
        message="Available data is limited"
      />
    </div>

    <div v-if="marketMaker.engagementOptions?.length" class="flex flex-col items-start gap-5">
      <div class="w-full border-t border-neutral-600" />
      <div class="font-semibold text-primal">
        Available engagement options
      </div>
      <div class="flex flex-wrap gap-2">
        <Badge
          v-for="option in marketMaker.engagementOptions"
          :key="option"
          size="md"
        >
          {{ EngagementOptionLabels[option] }}
        </Badge>
      </div>
    </div>

    <div
      v-for="section in exchangeSections"
      :key="section.title"
      class="flex flex-col items-start gap-5"
    >
      <div class="w-full border-t border-neutral-600" />
      <div class="font-semibold text-primal">
        {{ section.title }}
      </div>
      <div class="flex flex-wrap gap-2">
        <div class="flex gap-5 flex-wrap">
          <div v-for="item in section.items" :key="item.value" class="flex gap-1 items-center">
            <ExchangeAvatar size="sm" bordered :src="item.url" :alt="item.label" />
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="marketMaker.services?.length" class="flex flex-col items-start gap-5">
      <div class="w-full border-t border-neutral-600" />
      <div class="font-semibold text-primal">
        Ancillary services offered
      </div>
      <ul class="list-disc space-y-1 pl-5">
        <li v-for="item in marketMaker.services" :key="item">
          {{ item }}
        </li>
      </ul>
    </div>
  </div>
</template>
